import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Users, Building2,
  Phone, Mail, Globe, Edit, Trash2, Briefcase,
  ExternalLink, Tag, FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ExportDropdown } from '../components/shared/PageActions';
import PanelField from '../components/shared/PanelField';
import InfoCard from '../components/shared/InfoCard';
import CollapsiblePanel from '../components/shared/CollapsiblePanel';
import { BRAND_NAME, LOGO_URL, getDefaultCompanyInfo } from '../lib/brandConfig';
import { previewContactPdf, downloadContactPdf } from '../lib/infoCardPdfExport';
import ContactDocumentsTab from '../components/contacts/ContactDocumentsTab';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Category badge ──────────────────────────────────────────────────────────
function CategoryBadge({ category }) {
  const styles = {
    client:        { bg: '#dbeafe', color: '#1e40af' },
    supplier:      { bg: '#fce7f3', color: '#9d174d' },
    subcontractor: { bg: '#fef3c7', color: '#92400e' },
    consultant:    { bg: '#ede9fe', color: '#5b21b6' },
    internal:      { bg: '#d1fae5', color: '#065f46' },
  };
  const s = styles[category] || styles.client;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color }}
    >
      {category}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // ── Fetch contact ───────────────────────────────────────────────────────
  const fetchContact = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contacts/${id}`);
      setContact(res.data);
    } catch (err) {
      console.error('Failed to fetch contact:', err);
      if (err.response?.status === 404) navigate('/');
    }
  }, [id, navigate]);

  // ── Fetch clients list (for dropdown) ───────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/clients`).then(res => setClients(res.data?.data || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchContact().finally(() => setLoading(false));
  }, [fetchContact]);

  // ── Local field change ──────────────────────────────────────────────────
  const handleFieldChange = (field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { id: _id, contact_no, created_at, updated_at, ...updateData } = contact;
      const res = await axios.put(`${API}/contacts/${id}`, updateData);
      setContact(res.data);
    } catch (err) {
      console.error('Failed to save contact:', err);
    }
    setSaving(false);
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  // ── PDF ─────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();
  const handlePdfPreview = () => { if (contact) previewContactPdf(contact, companyInfo); };
  const handlePdfDownload = () => { if (contact) downloadContactPdf(contact, companyInfo); };
  const handlePrint = () => window.print();

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (!contact) return null;

  const linkedClient = contact.client_id
    ? clients.find(c => c.id === contact.client_id)
    : null;

  return (
    <div className="min-h-screen" style={{ background: '#e5e4df' }}>
      {/* Top bar */}
      <div className="bg-[#1a1a2e] py-3">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {contact.full_name}
              </h1>
              <span className="text-white/50 text-xs font-mono">{contact.contact_no}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete Contact</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {contact.full_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Body: left panel + right content */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6">
        <div className="flex gap-0 rounded-lg overflow-hidden shadow-xl" style={{ background: '#f8f7f4' }}>

          {/* ── Left Panel (Navy 320px) ─────────────────────────────────── */}
          <CollapsiblePanel collapsed={panelCollapsed} onToggle={() => setPanelCollapsed(!panelCollapsed)}>
                {/* Section: Personal */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Personal
                  </h3>
                  <PanelField label="Full Name" value={contact.full_name} onChange={v => handleFieldChange('full_name', v)} />
                  <PanelField label="Title" value={contact.title} onChange={v => handleFieldChange('title', v)} placeholder="e.g. Project Manager" />
                </div>

                {/* Section: Organization */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Organization
                  </h3>
                  <PanelField label="Company" value={contact.company} onChange={v => handleFieldChange('company', v)} />
                  <PanelField
                    label="Linked Client"
                    value={contact.client_id}
                    onChange={v => handleFieldChange('client_id', v)}
                    type="select"
                    options={[
                      { value: '', label: '— None —' },
                      ...clients.map(c => ({ value: c.id, label: c.company_name })),
                    ]}
                  />
                  <PanelField
                    label="Category"
                    value={contact.category}
                    onChange={v => handleFieldChange('category', v)}
                    type="select"
                    options={[
                      { value: 'client', label: 'Client' },
                      { value: 'supplier', label: 'Supplier' },
                      { value: 'subcontractor', label: 'Subcontractor' },
                      { value: 'consultant', label: 'Consultant' },
                      { value: 'internal', label: 'Internal' },
                    ]}
                  />
                </div>

                {/* Section: Contact Details */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Contact
                  </h3>
                  <PanelField label="Email" value={contact.email} onChange={v => handleFieldChange('email', v)} type="email" />
                  <PanelField label="Phone" value={contact.phone} onChange={v => handleFieldChange('phone', v)} />
                  <PanelField label="Mobile" value={contact.mobile} onChange={v => handleFieldChange('mobile', v)} />
                  <PanelField label="Website" value={contact.website} onChange={v => handleFieldChange('website', v)} />
                </div>

                {/* Section: Specialties */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Specialties
                  </h3>
                  <PanelField
                    label="Specialties"
                    value={Array.isArray(contact.specialties) ? contact.specialties.join(', ') : (contact.specialties || '')}
                    onChange={v => handleFieldChange('specialties', v.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="Comma-separated"
                  />
                </div>

                {/* Section: Notes */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Notes
                  </h3>
                  <PanelField label="Notes" value={contact.notes} onChange={v => handleFieldChange('notes', v)} type="textarea" />
                </div>
          </CollapsiblePanel>

          {/* ── Right Content ────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e4df', paddingLeft: 24, background: '#fff' }}>
              {[
                { key: 'details',   label: 'Details',   Icon: Users    },
                { key: 'documents', label: 'Documents', Icon: FileText },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '12px 16px',
                    fontSize: 13, fontWeight: 600,
                    color: activeTab === key ? '#1a1a2e' : '#9ca3af',
                    borderBottom: activeTab === key ? '2px solid #c9a84c' : '2px solid transparent',
                    background: 'none', border: 'none', cursor: 'pointer',
                    marginBottom: -1,
                  }}
                >
                  <Icon style={{ width: 14, height: 14 }} />
                  {label}
                </button>
              ))}
            </div>

            {/* Documents tab */}
            {activeTab === 'documents' && (
              <div className="p-6">
                <ContactDocumentsTab
                  documents={contact.documents || []}
                  onChange={docs => handleFieldChange('documents', docs)}
                />
              </div>
            )}

            {/* Details tab */}
            {activeTab === 'details' && <div className="p-6">
            {/* Contact header card */}
            <div className="flex items-start gap-6 mb-6 pb-6" style={{ borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
              {/* Avatar */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#1a1a2e' }}
              >
                <span className="text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {contact.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {contact.full_name}
                  </h2>
                  <CategoryBadge category={contact.category} />
                </div>
                {contact.title && <p className="text-sm text-[#6b7280] mb-1">{contact.title}</p>}
                {(contact.company || linkedClient) && (
                  <p className="text-sm text-[#6b7280] flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {contact.company || linkedClient?.company_name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="p-2 rounded-md hover:bg-blue-50 text-[#6b7280] hover:text-blue-600 transition-colors" title={`Email: ${contact.email}`}>
                    <Mail className="w-5 h-5" />
                  </a>
                )}
                {(contact.phone || contact.mobile) && (
                  <a href={`tel:${contact.phone || contact.mobile}`} className="p-2 rounded-md hover:bg-green-50 text-[#6b7280] hover:text-green-600 transition-colors" title={`Call: ${contact.phone || contact.mobile}`}>
                    <Phone className="w-5 h-5" />
                  </a>
                )}
                {contact.website && (
                  <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-md hover:bg-purple-50 text-[#6b7280] hover:text-purple-600 transition-colors" title={`Website: ${contact.website}`}>
                    <Globe className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <InfoCard icon={Mail} label="Email" value={contact.email ? <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a> : '—'} />
              <InfoCard icon={Phone} label="Phone" value={contact.phone || '—'} />
              <InfoCard icon={Phone} label="Mobile" value={contact.mobile || '—'} />
              <InfoCard icon={Globe} label="Website" value={contact.website ? <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{contact.website}</a> : '—'} />
              <InfoCard icon={Building2} label="Company" value={contact.company || '—'} />
              <InfoCard icon={Building2} label="Linked Client" value={linkedClient ? <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/clients/${contact.client_id}`)}>{linkedClient.company_name}</span> : '—'} />
            </div>

            {/* Specialties */}
            {contact.specialties && contact.specialties.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <Tag className="w-4 h-4 text-[#c9a84c]" />
                  Specialties
                </h3>
                <div className="flex flex-wrap gap-2">
                  {contact.specialties.map((s, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: '#ede9fe', color: '#5b21b6' }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {contact.notes && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Notes</h3>
                <div className="p-4 rounded-md text-sm text-[#2c2c2c] whitespace-pre-wrap" style={{ background: '#f3f2ef', border: '1px solid #e5e4df' }}>
                  {contact.notes}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="flex gap-4 text-xs text-[#9ca3af]">
              <span>Created: {contact.created_at ? new Date(contact.created_at).toLocaleDateString() : '—'}</span>
              <span>Updated: {contact.updated_at ? new Date(contact.updated_at).toLocaleDateString() : '—'}</span>
            </div>
            </div>}{/* end details tab */}

          </div>

        </div>
      </div>
    </div>
  );
}
